/**
 * ⌘K Results — parallel list `q` (no megendpoint) — plan/35 Wave F.
 */
import { listPosTickets } from "@/shared/api/pos";
import { listPurchaseOrders } from "@/shared/api/purchasing";
import { listParties } from "@/shared/api/parties";
import { listSalesOrders } from "@/shared/api/sales";
import { canSeeCompanyNav } from "./nav-math";
import { hasActionPermission } from "./permission-client";
import type { CommandItem } from "./command-math";

const LIMIT = 8;

export function recentToCommandItems(
  companySlug: string,
  docs: readonly {
    kind: string;
    id: string;
    label: string;
    path: string;
    hint?: string;
  }[],
): CommandItem[] {
  return docs.map((d) => ({
    id: `recent-${d.kind}-${d.id}`,
    label: d.label,
    hint: d.hint,
    group: "Recent" as const,
    path: d.path.startsWith("/") ? d.path : `/c/${companySlug}${d.path}`,
  }));
}

export async function searchDeskCommandResults(input: {
  companySlug: string;
  held: readonly string[];
  query: string;
  signal?: AbortSignal;
}): Promise<CommandItem[]> {
  const q = input.query.trim();
  if (q.length < 2) return [];

  const { companySlug, held, signal } = input;
  const canSales =
    canSeeCompanyNav(held, "sales") || hasActionPermission(held, "*");
  const canPurchasing =
    canSeeCompanyNav(held, "purchasing") || hasActionPermission(held, "*");
  const canPos = canSeeCompanyNav(held, "pos") || hasActionPermission(held, "*");

  const tasks: Promise<CommandItem[]>[] = [];

  if (canSales) {
    tasks.push(
      listSalesOrders(companySlug, { q, limit: LIMIT, signal }).then((page) =>
        (page.orders ?? []).map((o) => ({
          id: `hit-so-${o.id}`,
          label: o.number,
          hint: o.customerName,
          group: "Results" as const,
          path: `/c/${companySlug}/sales/orders/${o.id}`,
        })),
      ),
    );
    tasks.push(
      listParties(companySlug, {
        role: "customer",
        q,
        limit: LIMIT,
        signal,
      }).then((page) =>
        (page.parties ?? []).map((c) => ({
          id: `hit-cust-${c.id}`,
          label: c.name,
          hint: c.code ? `Customer · ${c.code}` : "Customer",
          group: "Results" as const,
          path: `/c/${companySlug}/contacts/${c.id}`,
        })),
      ),
    );
  }
  if (canPurchasing) {
    tasks.push(
      listPurchaseOrders(companySlug, { q, limit: LIMIT, signal }).then(
        (page) =>
          (page.orders ?? []).map((o) => ({
            id: `hit-po-${o.id}`,
            label: o.number,
            hint: o.supplierName,
            group: "Results" as const,
            path: `/c/${companySlug}/purchasing/orders/${o.id}`,
          })),
      ),
    );
    tasks.push(
      listParties(companySlug, {
        role: "supplier",
        q,
        limit: LIMIT,
        signal,
      }).then((page) =>
        (page.parties ?? []).map((s) => ({
          id: `hit-sup-${s.id}`,
          label: s.name,
          hint: s.code ? `Supplier · ${s.code}` : "Supplier",
          group: "Results" as const,
          path: `/c/${companySlug}/contacts/${s.id}`,
        })),
      ),
    );
  }
  if (canPos) {
    tasks.push(
      listPosTickets(companySlug, { q, limit: LIMIT, signal }).then((page) =>
        (page.tickets ?? []).map((t) => ({
          id: `hit-pos-${t.id}`,
          label: t.number,
          hint: "POS ticket",
          group: "Results" as const,
          path: `/c/${companySlug}/pos/tickets/${t.id}`,
        })),
      ),
    );
  }

  const settled = await Promise.allSettled(tasks);
  const out: CommandItem[] = [];
  for (const s of settled) {
    if (s.status === "fulfilled") out.push(...s.value);
  }
  return out;
}
